// TypeScript port of api-recorder-extension/src/postman-converter.js
// Converts Gherkin scenarios into a Postman Collection v2.1 JSON object.

import type { ParsedGherkinCase } from './aiOutputParser';
import { parseSteps, parseBackground, type StepAssertion } from './playwrightConverter';

// ── Public API ────────────────────────────────────────────────────────────────

export function scenariosToPostman(cases: ParsedGherkinCase[], rawText: string, featureName: string): object | null {
  if (!cases || cases.length === 0) return null;

  const name = featureName || (cases[0] && cases[0].featureName) || 'API Tests';
  const bg   = parseBackground(rawText);
  const now  = new Date().toISOString().slice(0, 19).replace('T', ' ');

  return {
    info: {
      name,
      _postman_id: generateUuid(),
      description: `Generated by FINT Portal — ${now}`,
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    item: cases.map(tc => scenarioToItem(tc, bg)),
  };
}

// ── Item builder ──────────────────────────────────────────────────────────────

function scenarioToItem(
  tc: ParsedGherkinCase,
  bg: { baseURL: string | null; headers: Record<string, string> }
): object {
  const steps   = parseSteps(tc.lines);
  const baseURL = steps.baseURL || bg.baseURL || '';
  const headers = { ...bg.headers, ...steps.headers };
  const method  = (steps.method || 'GET').toUpperCase();
  const path    = steps.path || '/';
  const fullUrl = path.startsWith('http') ? path : `${baseURL}${path}`;

  const headerArray: Array<{ key: string; value: string; type: string }> =
    Object.entries(headers).map(([key, value]) => ({ key, value, type: 'text' }));

  let bodyObj: object | undefined;
  if (steps.body !== null) {
    if (!headerArray.some(h => h.key.toLowerCase() === 'content-type')) {
      headerArray.push({ key: 'Content-Type', value: 'application/json', type: 'text' });
    }
    bodyObj = {
      mode: 'raw',
      raw: steps.body,
      options: { raw: { language: 'json' } },
    };
  }

  const testLines = buildTestScript(steps.assertions);

  const item: Record<string, unknown> = {
    name: `${tc.id} — ${tc.scenarioName}`,
    event: testLines.length > 0 ? [{
      listen: 'test',
      script: { exec: testLines, type: 'text/javascript' },
    }] : [],
    request: {
      method,
      header: headerArray,
      url: buildUrlObject(fullUrl),
    },
    response: [],
  };

  if (bodyObj) (item.request as Record<string, unknown>).body = bodyObj;

  return item;
}

// ── pm.test() script builder ──────────────────────────────────────────────────

function buildTestScript(assertions: StepAssertion[]): string[] {
  if (!assertions || assertions.length === 0) return [];

  const lines: string[] = [];
  const bodyAssertions: StepAssertion[] = [];
  let statusAssertion: StepAssertion | null = null;

  for (const a of assertions) {
    if (a.type === 'status') statusAssertion = a;
    else bodyAssertions.push(a);
  }

  if (statusAssertion) {
    lines.push(`pm.test("Status is ${statusAssertion.value}", function () {`);
    lines.push(`    pm.response.to.have.status(${statusAssertion.value});`);
    lines.push(`});`);
  }

  const needsBody = bodyAssertions.some(a => ['hasField', 'fieldEquals', 'emptyArray'].includes(a.type));
  if (needsBody) lines.push(`const json = pm.response.json();`);

  for (const a of bodyAssertions) {
    switch (a.type) {
      case 'hasField':
        lines.push(`pm.test("Field '${esc(a.field)}' exists", function () {`);
        lines.push(`    pm.expect(json).to.have.property('${esc(a.field)}');`);
        lines.push(`});`);
        break;
      case 'fieldEquals':
        lines.push(`pm.test("'${esc(a.field)}' equals '${esc(a.value)}'", function () {`);
        lines.push(`    pm.expect(String(json['${esc(a.field)}'])).to.eql('${esc(a.value)}');`);
        lines.push(`});`);
        break;
      case 'emptyArray':
        lines.push(`pm.test("Body is an empty array", function () {`);
        lines.push(`    pm.expect(json).to.be.an('array').that.is.empty;`);
        lines.push(`});`);
        break;
      case 'responseTime':
        lines.push(`pm.test("Response time < ${a.ms}ms", function () {`);
        lines.push(`    pm.expect(pm.response.responseTime).to.be.below(${a.ms});`);
        lines.push(`});`);
        break;
      case 'responseHeader':
        lines.push(`pm.test("Header '${esc(a.header)}' contains '${esc(a.value)}'", function () {`);
        lines.push(`    pm.expect(pm.response.headers.get('${esc(a.header)}')).to.include('${esc(a.value)}');`);
        lines.push(`});`);
        break;
    }
  }

  return lines;
}

// ── URL object builder ────────────────────────────────────────────────────────

function buildUrlObject(fullUrl: string): object {
  try {
    const parsed    = new URL(fullUrl);
    const hostParts = parsed.hostname.split('.');
    const pathParts = parsed.pathname.split('/').filter(p => p.length > 0);
    const queryArr: Array<{ key: string; value: string }> = [];
    parsed.searchParams.forEach((value, key) => queryArr.push({ key, value }));

    const obj: Record<string, unknown> = {
      raw:      fullUrl,
      protocol: parsed.protocol.replace(':', ''),
      host:     hostParts,
      path:     pathParts,
    };
    if (queryArr.length > 0) obj.query = queryArr;
    return obj;
  } catch {
    return { raw: fullUrl };
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function esc(s: string | number | undefined): string {
  return String(s ?? '').replace(/'/g, "\\'");
}
